import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PlanCard from "@/components/billing/PlanCard";
import CreditMeters from "@/components/billing/CreditMeters";
import PackShop from "@/components/billing/PackShop";
import TransactionsList from "@/components/billing/TransactionsList";
import UsageHistory from "@/components/billing/UsageHistory";

/* Same page for members and admins — admins see exactly what a member sees (their status is "exempt"). */
export default function BillingPage() {
  const { refreshMe } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/billing/me");
      setData(res);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("cancelled")) {
      toast.message("Checkout cancelled — nothing was charged");
      params.delete("cancelled");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChanged = async () => {
    await load();
    await refreshMe();
  };

  const status = data?.status;
  const paidId = params.get("paid");
  const paidTxn = paidId && data ? (data.transactions || []).find((t) => t.id === paidId && t.status === "paid") : null;
  const clearPaid = useCallback(() => {
    const p = new URLSearchParams(params);
    p.delete("paid");
    setParams(p, { replace: true });
  }, [params, setParams]);

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
          <CreditCard className="h-3.5 w-3.5" /> Account
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-display">Billing &amp; Credits</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Your monthly plan, credit balances, top-up packs and receipts. Payments run through Authorize.Net's secure page.
        </p>
      </div>

      {err ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="billing-error">{err}</div>
      ) : !data ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-8" data-testid="billing-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading billing…
        </div>
      ) : (
        <>
          {status?.status === "not_live" ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="billing-not-live-note">
              Billing hasn't been switched on yet — every feature is free for now. When it goes live, existing members get a complimentary period before the first charge.
            </div>
          ) : null}
          <PlanCard data={data} onChanged={onChanged} />
          <CreditMeters credits={data.credits} pools={data.pools} plan={data.plan} />
          <PackShop data={data} onChanged={onChanged} paidTxn={paidTxn} onPaidConsumed={clearPaid} />
          <TransactionsList rows={data.transactions} />
          <UsageHistory rows={data.usage} />
        </>
      )}
    </div>
  );
}
